import { useState, useMemo, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Typography, Box, Select, MenuItem, FormControl } from '@mui/material';
import './UserTeacherLineChart.css';
import { axiosInstance } from '../../../../../utils/tokenUtils';

const timeOptions = [
  { value: 'today', label: 'Today' },
  { value: '1_week', label: '1 Week' },
  { value: '2_weeks', label: '2 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '3_months', label: '3 Months' },
  { value: '6_months', label: '6 Months' },
  { value: '1_year', label: '1 Year' },
];

const shimmerStyle = {
  width: '100%',
  height: 300,
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
  borderRadius: 8,
};

const UserTeacherLineChart = ({ title = 'User Growth Chart' }) => {
  const [timeFilter, setTimeFilter] = useState('1_year');
  const [loading, setLoading] = useState(true);
  const [apiData, setApiData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch data when component mounts or timeFilter changes
  useEffect(() => {
    let isMounted = true;

    const fetchDataAsync = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.get(
          `/admin/dashboard/user-growth-chart?period=${timeFilter}`
        );
        if (isMounted) {
          setApiData(response.data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch data');
          console.error('Error fetching chart data:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDataAsync();

    return () => {
      isMounted = false;
    };
  }, [timeFilter]);

  // Handle filter change
  const handleFilterChange = event => {
    setTimeFilter(event.target.value);
  };

  // Transform API data to chart format
  const chartData = useMemo(() => {
    if (!apiData || !apiData.data_points) return [];

    return apiData.data_points.map(point => ({
      name: point.period,
      registrationToBooking: point.registration_booking_ratio || 0,
      bookingToAvailability: point.booking_availability_ratio || 0,
      registrations: point.registrations || 0,
      bookings: point.bookings || 0,
      availabilitySlots: point.availability_slots || 0,
    }));
  }, [apiData]);

  const maxValue = useMemo(() => {
    if (!chartData.length) return 100;
    const allValues = [
      ...chartData.map(item => item.registrationToBooking),
      ...chartData.map(item => item.bookingToAvailability),
    ];
    const max = Math.max(...allValues, 0);

    // Handle ratio values (percentages) - if max is less than 1, treat as decimal and convert to percentage
    if (max <= 1) {
      const maxPercentage = max * 100;
      // If both values are less than 2%, set max to 5%
      if (maxPercentage < 2) {
        return 5;
      }
      return Math.ceil(maxPercentage * 1.2) || 100;
    }
    // If max is already in percentage format (greater than 1), use as is
    // If both values are less than 2%, set max to 5%
    if (max < 2) {
      return 5;
    }
    return Math.ceil(max * 1.2) || 100;
  }, [chartData]);

  const getYAxisTicks = max => {
    // Handle very small percentage values (0-5%)
    if (max <= 5) {
      return [0, 1, 2, 3, 4, 5];
    }
    // Handle percentage values (0-100%)
    if (max <= 100) {
      if (max <= 10) return [0, 2, 4, 6, 8, 10];
      if (max <= 25) return [0, 5, 10, 15, 20, 25];
      if (max <= 50) return [0, 10, 20, 30, 40, 50];
      return [0, 20, 40, 60, 80, 100];
    }

    // Handle larger values (fallback for any edge cases)
    if (max <= 500) return [0, 100, 200, 300, 400, 500];
    if (max <= 1000) return [0, 200, 400, 600, 800, 1000];
    if (max <= 2000) return [0, 400, 800, 1200, 1600, 2000];
    if (max <= 5000) return [0, 1000, 2000, 3000, 4000, 5000];
    if (max <= 10000) return [0, 2000, 4000, 6000, 8000, 10000];
    return [0, 5000, 10000, 15000, 20000];
  };
  const yAxisTicks = getYAxisTicks(maxValue);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="user-teacher-custom-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="teacher">{`Registration to Booking Ratio: ${data.registrationToBooking.toFixed(2)}%`}</p>
          <p className="student">{`Booking to Availability Ratio: ${data.bookingToAvailability.toFixed(2)}%`}</p>
          <p className="details">{`Registrations: ${data.registrations}`}</p>
          <p className="details">{`Bookings: ${data.bookings}`}</p>
          <p className="details">{`Availability Slots: ${data.availabilitySlots}`}</p>
        </div>
      );
    }
    return null;
  };

  if (error) {
    return (
      <div className="user-teacher-analytics-container">
        <div className="user-teacher-header">
          <Typography className="user-teacher-header-title">{title}</Typography>
        </div>
        <div className="user-teacher-chart-container">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              color: '#666',
              fontSize: '14px',
            }}
          >
            Error loading chart data: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-teacher-analytics-container">
      <div className="user-teacher-header">
        <Typography className="user-teacher-header-title">{title}</Typography>
        <div className="user-teacher-time-filters">
          <FormControl variant="outlined" size="small">
            <Select
              value={timeFilter}
              onChange={handleFilterChange}
              className="time-filter-dropdown"
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      fontSize: '14px',
                      fontFamily: 'Poppins',
                    },
                  },
                },
              }}
            >
              {timeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
      <div className="user-teacher-chart-container">
        {loading ? (
          <div style={shimmerStyle} />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 10 }}>
              <defs>
                <linearGradient id="colorRegistrationToBooking" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1a73e8" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#1a73e8" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="colorBookingToAvailability" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34d399" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#34d399" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#666', fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#666', fontSize: 12 }}
                tickFormatter={value => {
                  // For percentage values (0-100 range), show as percentage
                  if (maxValue <= 100) {
                    return `${value}%`;
                  }
                  // For larger values, use K format
                  if (value >= 1000) return `${value / 1000}K`;
                  return value;
                }}
                domain={[0, maxValue]}
                ticks={yAxisTicks}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="registrationToBooking"
                stroke="#1a73e8"
                strokeWidth={2}
                fill="url(#colorRegistrationToBooking)"
                fillOpacity={1}
              />
              <Area
                type="monotone"
                dataKey="bookingToAvailability"
                stroke="transparent"
                fill="url(#colorBookingToAvailability)"
                fillOpacity={1}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
        <div className="user-teacher-legend">
          <div className="user-teacher-legend-item">
            <div className="user-teacher-legend-color registration"></div>
            <span>Registration to Booking Ratio</span>
          </div>
          <div className="user-teacher-legend-item">
            <div className="user-teacher-legend-color booking"></div>
            <span>Booking to Availability Ratio</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserTeacherLineChart;
